import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  AlternateEmail as MentionIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { MentionStats } from '../../types/mention';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

/**
 * MentionAnalytics Component
 * Analytics dashboard showing mention usage statistics and trends
 *
 * Features:
 * - Total mentions over time
 * - Mentions by content type (comments, messages, notes, memories)
 * - Most mentioned users
 * - Most active mentioners
 * - Date range filtering
 * - Export to CSV
 */
const MentionAnalytics: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [stats, setStats] = useState<{
    totalMentions: number;
    byContentType: Record<string, number>;
    topMentioned: Array<{ userId: string; count: number; displayName?: string }>;
    topMentioners: Array<{ userId: string; count: number; displayName?: string }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(30); // Days

  /**
   * Load mention analytics data
   */
  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  /**
   * Load analytics data from Firestore
   */
  const loadAnalytics = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range
      const startDate = startOfDay(subDays(new Date(), dateRange));
      const startDateStr = format(startDate, 'yyyy-MM-dd');

      // Query mention stats within date range
      const statsRef = collection(db, 'mentionStats');
      const q = query(
        statsRef,
        where('period', '>=', startDateStr),
        orderBy('period', 'desc'),
        limit(1000)
      );

      const snapshot = await getDocs(q);
      const allStats: MentionStats[] = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as MentionStats[];

      // Aggregate statistics
      let totalMentions = 0;
      const byContentType: Record<string, number> = {
        comment: 0,
        message: 0,
        note: 0,
        memory: 0,
        feature_request: 0,
      };
      const mentionedUsers = new Map<string, number>();
      const mentionerUsers = new Map<string, number>();

      allStats.forEach(stat => {
        totalMentions += stat.mentionCount;

        // Aggregate by content type
        Object.keys(stat.byContentType).forEach(type => {
          byContentType[type] = (byContentType[type] || 0) + stat.byContentType[type as keyof typeof stat.byContentType];
        });

        // Track most mentioned users
        mentionedUsers.set(stat.userId, (mentionedUsers.get(stat.userId) || 0) + stat.mentionCount);

        // Track top mentioners
        stat.topMentioners.forEach(mentioner => {
          mentionerUsers.set(
            mentioner.userId,
            (mentionerUsers.get(mentioner.userId) || 0) + mentioner.count
          );
        });
      });

      // Get top 10 mentioned users
      const topMentionedRaw = Array.from(mentionedUsers.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([userId, count]) => ({ userId, count }));

      // Get top 10 mentioners
      const topMentionersRaw = Array.from(mentionerUsers.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([userId, count]) => ({ userId, count }));

      // Fetch user display names
      const userIds = [...new Set([
        ...topMentionedRaw.map(u => u.userId),
        ...topMentionersRaw.map(u => u.userId),
      ])];

      const usersRef = collection(db, 'users');
      const userDocs = await Promise.all(
        userIds.map(async (userId) => {
          const userSnapshot = await getDocs(query(usersRef, where('__name__', '==', userId)));
          return userSnapshot.docs[0]?.data();
        })
      );

      const userMap = new Map<string, string>();
      userDocs.forEach((userData, index) => {
        if (userData) {
          userMap.set(userIds[index], userData.displayName || userData.email || 'Unknown');
        }
      });

      // Add display names to top lists
      const topMentioned = topMentionedRaw.map(user => ({
        ...user,
        displayName: userMap.get(user.userId),
      }));

      const topMentioners = topMentionersRaw.map(user => ({
        ...user,
        displayName: userMap.get(user.userId),
      }));

      setStats({
        totalMentions,
        byContentType,
        topMentioned,
        topMentioners,
      });
    } catch (err: any) {
      console.error('Error loading mention analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Export analytics to CSV
   */
  const handleExport = (): void => {
    if (!stats) return;

    const csvRows = [
      ['Metric', 'Value'],
      ['Total Mentions', stats.totalMentions.toString()],
      ['', ''],
      ['Mentions by Content Type', ''],
      ...Object.entries(stats.byContentType).map(([type, count]) => [
        type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' '),
        count.toString(),
      ]),
      ['', ''],
      ['Top Mentioned Users', ''],
      ['User', 'Mention Count'],
      ...stats.topMentioned.map(user => [
        user.displayName || user.userId,
        user.count.toString(),
      ]),
      ['', ''],
      ['Top Mentioners', ''],
      ['User', 'Mentions Given'],
      ...stats.topMentioners.map(user => [
        user.displayName || user.userId,
        user.count.toString(),
      ]),
    ];

    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mention-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  /**
   * Render error state
   */
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadAnalytics}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Mention Analytics
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View statistics and trends for @mentions across the platform
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value as number)}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={365}>Last year</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAnalytics}
        >
          Refresh
        </Button>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Mentions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MentionIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Mentions</Typography>
              </Box>
              <Typography variant="h3">{stats?.totalMentions.toLocaleString() || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                Last {dateRange} days
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Comments */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Comments</Typography>
              <Typography variant="h3">{stats?.byContentType.comment.toLocaleString() || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.totalMentions ?
                  `${((stats.byContentType.comment / stats.totalMentions) * 100).toFixed(1)}% of total`
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Messages */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Messages</Typography>
              <Typography variant="h3">{stats?.byContentType.message.toLocaleString() || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.totalMentions ?
                  `${((stats.byContentType.message / stats.totalMentions) * 100).toFixed(1)}% of total`
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              <Typography variant="h3">{stats?.byContentType.note.toLocaleString() || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.totalMentions ?
                  `${((stats.byContentType.note / stats.totalMentions) * 100).toFixed(1)}% of total`
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Memories */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Memories</Typography>
              <Typography variant="h3">{stats?.byContentType.memory.toLocaleString() || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.totalMentions ?
                  `${((stats.byContentType.memory / stats.totalMentions) * 100).toFixed(1)}% of total`
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables */}
      <Grid container spacing={3}>
        {/* Top Mentioned Users */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Top Mentioned Users</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Mentions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.topMentioned.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stats?.topMentioned.map((user, index) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Chip
                            label={index + 1}
                            size="small"
                            color={index < 3 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{user.displayName || user.userId}</TableCell>
                        <TableCell align="right">
                          <strong>{user.count.toLocaleString()}</strong>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Top Mentioners */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Most Active Mentioners</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Mentions Given</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.topMentioners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stats?.topMentioners.map((user, index) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Chip
                            label={index + 1}
                            size="small"
                            color={index < 3 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{user.displayName || user.userId}</TableCell>
                        <TableCell align="right">
                          <strong>{user.count.toLocaleString()}</strong>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MentionAnalytics;
